import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import {
  PaymentMethodType,
  PaymentProvider,
  PaymentStatus,
  type PaymentMethodType as PaymentMethodTypeT,
  type PaymentProvider as PaymentProviderT,
  type PaymentStatus as PaymentStatusT,
} from '@etiquetachef/types';
import {
  subscribeCrossTenantPayments,
  type CrossTenantPaymentsResult,
} from '@/lib/firestoreTenantDetail';
import { FinanceFilters } from '@/features/finance/FinanceFilters';
import { TransactionsTable } from '@/features/finance/TransactionsTable';

const STATUS_OPTIONS: { value: PaymentStatusT; labelKey: string }[] = [
  { value: PaymentStatus.CONFIRMED, labelKey: 'admin.finance.transactions.statusOption.CONFIRMED' },
  { value: PaymentStatus.PROCESSING, labelKey: 'admin.finance.transactions.statusOption.PROCESSING' },
  { value: PaymentStatus.FAILED, labelKey: 'admin.finance.transactions.statusOption.FAILED' },
  { value: PaymentStatus.REFUNDED, labelKey: 'admin.finance.transactions.statusOption.REFUNDED' },
  {
    value: PaymentStatus.PARTIALLY_REFUNDED,
    labelKey: 'admin.finance.transactions.statusOption.PARTIALLY_REFUNDED',
  },
];

const PROVIDER_OPTIONS: { value: PaymentProviderT; labelKey: string }[] = [
  { value: PaymentProvider.STRIPE, labelKey: 'admin.finance.transactions.providerOption.stripe' },
  { value: PaymentProvider.ASAAS, labelKey: 'admin.finance.transactions.providerOption.asaas' },
  { value: PaymentProvider.MERCADOPAGO, labelKey: 'admin.finance.transactions.providerOption.mercadopago' },
  { value: PaymentProvider.MANUAL, labelKey: 'admin.finance.transactions.providerOption.manual' },
];

const METHOD_OPTIONS: { value: PaymentMethodTypeT; labelKey: string }[] = [
  { value: PaymentMethodType.CARD, labelKey: 'admin.finance.transactions.methodOption.card' },
  { value: PaymentMethodType.PIX, labelKey: 'admin.finance.transactions.methodOption.pix' },
  { value: PaymentMethodType.BOLETO, labelKey: 'admin.finance.transactions.methodOption.boleto' },
  { value: PaymentMethodType.SEPA_DEBIT, labelKey: 'admin.finance.transactions.methodOption.sepa_debit' },
  { value: PaymentMethodType.MANUAL, labelKey: 'admin.finance.transactions.methodOption.manual' },
];

/**
 * Tela `/finance/transactions` — pagamentos de todas as empresas, com filtro
 * por status, PSP e meio de pagamento, e busca local.
 *
 * Origem: sem issue — commit 973be8d9c, 2026-05-05 (Sprint 5 PR H, painel
 * financeiro).
 *
 * Contrato: assina `subscribeCrossTenantPayments({ status, provider,
 * methodType })`; a busca (tenant, fatura, id) filtra no cliente. Só leitura.
 * Valores em centavos, convertidos no `TransactionsTable`.
 *
 * Usado por: rota `finance/transactions` (`routes/router.tsx`), menu Cobrança
 * (`AdminLayout`). Spec: `docs/screens/finance-transactions.md`.
 *
 * Dados: collection group `payments`, `orderBy('created_at','desc')` com
 * igualdade em `status`, `provider` e/ou `payment_method_type`; índices
 * `COLLECTION_GROUP` de cada um com `created_at`; rule `match
 * /{path=**}/payments/{paymentId}` (só super-admin). Pagamentos de sandbox
 * são filtrados em memória (ver `subscribeCrossTenantPayments`).
 *
 * Dicas: dois ou três filtros juntos não têm índice composto próprio —
 * dependem de o Firestore combinar os índices de um filtro só. Se vier
 * `failed-precondition`, é o índice da combinação que falta.
 */
export function TransactionsPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<PaymentStatusT | null>(null);
  const [provider, setProvider] = useState<PaymentProviderT | null>(null);
  const [methodType, setMethodType] = useState<PaymentMethodTypeT | null>(null);
  const [search, setSearch] = useState('');
  const [data, setData] = useState<CrossTenantPaymentsResult>({ kind: 'LOADING' });

  useEffect(() => {
    setData({ kind: 'LOADING' });
    return subscribeCrossTenantPayments({ status, provider, methodType }, setData);
  }, [status, provider, methodType]);

  const filtered = useMemo(() => {
    if (data.kind !== 'READY') return [];
    const needle = search.trim().toLowerCase();
    if (needle === '') return data.payments;
    return data.payments.filter(
      (p) =>
        p.tenant_id.toLowerCase().includes(needle) ||
        p.invoice_id.toLowerCase().includes(needle) ||
        p.id.toLowerCase().includes(needle),
    );
  }, [data, search]);

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={t('admin.finance.transactions.subtitle')}>
          {t('admin.finance.transactions.title')}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Container header={<Header variant="h2">{t('admin.finance.filters.title')}</Header>}>
          <FinanceFilters
            statusValue={status}
            statusOptions={STATUS_OPTIONS}
            onStatusChange={setStatus}
            secondaryValue={provider}
            secondaryOptions={PROVIDER_OPTIONS}
            onSecondaryChange={setProvider}
            secondaryLabel={t('admin.finance.filters.provider')}
            methodValue={methodType}
            methodOptions={METHOD_OPTIONS}
            onMethodChange={setMethodType}
            searchValue={search}
            onSearchChange={setSearch}
          />
        </Container>

        {data.kind === 'LOADING' && (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
          </Box>
        )}
        {data.kind === 'ERROR' && <Alert type="error">{t('admin.finance.transactions.errorLoad')}</Alert>}
        {data.kind === 'READY' && <TransactionsTable payments={filtered} locale={i18n.language} />}
      </SpaceBetween>
    </ContentLayout>
  );
}
